import { Modal } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { ApimHandler } from '@api/apim-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { CreateServicePrincipalRequest, ServicePrincipal } from '@pages/api-portal/types';

const EMPTY_FORM: CreateServicePrincipalRequest = { name: '', description: '', status: 'active' };

/*
 * Manages the service principals list, the create form, the one-time credentials dialog,
 * and delete confirmation for the /admin/service-principals screen.
 */
export function useServicePrincipals() {
    const [servicePrincipals, setServicePrincipals] = useState<ServicePrincipal[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [createForm, setCreateForm] = useState<CreateServicePrincipalRequest>(EMPTY_FORM);
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const [isCredentialsDialogOpen, setIsCredentialsDialogOpen] = useState(false);
    const [createdPrincipal, setCreatedPrincipal] = useState<ServicePrincipal | null>(null);

    const [isDeleting, setIsDeleting] = useState(false);

    /*
     * Loads the full service principals list.
     */
    const fetchServicePrincipals = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await ApimHandler.fetchServicePrincipals();
            setServicePrincipals(data);
        } catch {
            errorAlert('Failed to load service principals.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchServicePrincipals();
    }, [fetchServicePrincipals]);

    /*
     * Opens the create dialog with a blank form.
     */
    const handleOpenCreateDialog = useCallback(() => {
        setCreateForm(EMPTY_FORM);
        setCreateError(null);
        setIsCreateDialogOpen(true);
    }, []);

    /*
     * Validates and submits the create form, then shows the one-time credentials dialog on success.
     */
    const handleCreate = useCallback(async () => {
        if (!createForm.name.trim()) return;

        if (/\s/.test(createForm.name)) {
            setCreateError('Name cannot contain spaces. Use hyphens (-) or underscores (_) instead.');
            return;
        }

        setIsCreating(true);
        setCreateError(null);
        try {
            const created = await ApimHandler.createServicePrincipal(createForm);
            setIsCreateDialogOpen(false);
            setCreatedPrincipal(created);
            setIsCredentialsDialogOpen(true);
            fetchServicePrincipals();
        } catch (error) {
            setCreateError(error instanceof Error ? error.message : 'Failed to create service principal.');
        } finally {
            setIsCreating(false);
        }
    }, [createForm, fetchServicePrincipals]);

    /*
     * Closes the one-time credentials dialog and clears the stored credentials.
     */
    const handleCloseCredentialsDialog = useCallback(() => {
        setIsCredentialsDialogOpen(false);
        setCreatedPrincipal(null);
    }, []);

    /*
     * Confirms and deletes a service principal, warning that the linked DataStudio user is
     * also removed.
     */
    const handleDeleteRequest = useCallback(
        (principal: ServicePrincipal) => {
            Modal.confirm({
                className: 'apimModal',
                title: 'Delete Service Principal',
                content: `Are you sure you want to delete "${principal.name}"? This will also delete the corresponding DataStudio user. This action cannot be undone.`,
                okText: 'Delete',
                cancelText: 'Cancel',
                okButtonProps: { style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' } },
                onOk: async () => {
                    setIsDeleting(true);
                    try {
                        await ApimHandler.deleteServicePrincipal(principal.id);
                        successAlert(`Deleted service principal "${principal.name}".`);
                        setServicePrincipals((prev) => prev.filter((sp) => sp.id !== principal.id));
                    } catch {
                        errorAlert('Failed to delete service principal.');
                    } finally {
                        setIsDeleting(false);
                    }
                },
            });
        },
        [],
    );

    return {
        servicePrincipals,
        isLoading,
        isCreateDialogOpen,
        setIsCreateDialogOpen,
        createForm,
        setCreateForm,
        isCreating,
        createError,
        setCreateError,
        isCredentialsDialogOpen,
        createdPrincipal,
        isDeleting,
        handleOpenCreateDialog,
        handleCreate,
        handleCloseCredentialsDialog,
        handleDeleteRequest,
    };
}
